import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import Button from "../../components/common/Button";
import DataTable from "../../components/common/DataTable";
import SearchableDropdown from "../../components/common/SearchableDropdown";
import reportService from "../../services/reportService";

function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

function ContainerWiseReportPage() {
  const navigate = useNavigate();
  const { blContainerId } = useEncryptedParams();
  const [selectedContainerId, setSelectedContainerId] = useState(blContainerId || "");
  const [containers, setContainers] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isContainersLoading, setIsContainersLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setSelectedContainerId(blContainerId || "");
  }, [blContainerId]);

  const loadActiveContainers = useCallback(async () => {
    setIsContainersLoading(true);
    try {
      const data = await reportService.getActiveContainers();
      setContainers(data);
    } catch (err) {
      setError(err?.message || "Failed to load active containers.");
    } finally {
      setIsContainersLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (containerId) => {
    setIsLoading(true);
    setError("");
    try {
      const report = await reportService.getContainerWiseReport(containerId);
      setRows(report.items);
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to load container-wise report.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActiveContainers();
  }, [loadActiveContainers]);

  useEffect(() => {
    loadReport(blContainerId);
  }, [blContainerId, loadReport]);

  const containerOptions = useMemo(() => {
    const options = containers.map((container) => {
      const id = container.blContainerId || container.id;
      const containerNo = container.containerNo || container.containerNumber || `Container ${id}`;
      const containerType = container.containerType ? ` (${container.containerType})` : "";
      return {
        value: String(id),
        label: `${containerNo}${containerType}`,
      };
    });

    return [{ value: "", label: "All active containers" }, ...options];
  }, [containers]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, item) => ({
          totalAdditionStock: acc.totalAdditionStock + Number(item.totalAdditionStock || 0),
          totalDeductionStock: acc.totalDeductionStock + Number(item.totalDeductionStock || 0),
          remainingStock: acc.remainingStock + Number(item.remainingStock || 0),
        }),
        { totalAdditionStock: 0, totalDeductionStock: 0, remainingStock: 0 }
      ),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        key: "containerNo",
        label: "Container No",
        sortable: true,
        render: (item) => (
          <span className="font-bold text-slate-900 dark:text-white">{item.containerNo || "-"}</span>
        ),
      },
      { key: "containerType", label: "Type", sortable: true },
      {
        key: "totalAdditionStock",
        label: "Addition Stock (kg)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => formatWeight(item.totalAdditionStock),
      },
      {
        key: "totalDeductionStock",
        label: "Deduction Stock (kg)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => formatWeight(item.totalDeductionStock),
      },
      {
        key: "remainingStock",
        label: "Remaining Stock (kg)",
        sortable: true,
        headerClassName: "text-right",
        className: "text-right",
        render: (item) => (
          <span className="font-bold text-primary-700 dark:text-primary-300">
            {formatWeight(item.remainingStock)}
          </span>
        ),
      },
    ],
    []
  );

  const handleContainerChange = (event) => {
    const value = event.target.value;
    setSelectedContainerId(value);
    navigate(value ? `/reports/container-wise/${encrypt(value)}` : "/reports/container-wise");
  };

  const handleRefresh = () => {
    loadActiveContainers();
    loadReport(selectedContainerId);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 dark:border-slate-800 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Reports
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            Container-wise Report
          </h1>
        </div>

        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto lg:items-end">
          <div className="w-full sm:min-w-[280px]">
            <SearchableDropdown
              label="Active Container"
              name="blContainerId"
              value={selectedContainerId}
              onChange={handleContainerChange}
              options={containerOptions}
              placeholder={isContainersLoading ? "Loading containers..." : "All active containers"}
              disabled={isContainersLoading}
            />
          </div>
          <Button variant="secondary" onClick={handleRefresh} isLoading={isLoading || isContainersLoading}>
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm font-medium text-danger">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Addition Stock
          </p>
          <p className="mt-2 text-xl font-bold text-slate-950 dark:text-white">
            {formatWeight(totals.totalAdditionStock)}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Deduction Stock
          </p>
          <p className="mt-2 text-xl font-bold text-slate-950 dark:text-white">
            {formatWeight(totals.totalDeductionStock)}
          </p>
        </div>
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 shadow-sm dark:border-primary-900/50 dark:bg-primary-900/10">
          <p className="text-[11px] font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300">
            Remaining Stock
          </p>
          <p className="mt-2 text-xl font-bold text-primary-800 dark:text-primary-200">
            {formatWeight(totals.remainingStock)}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        searchPlaceholder="Search container report..."
        pageSize={10}
        scrollHeight="58vh"
      />
    </div>
  );
}

export default ContainerWiseReportPage;
